import { useEffect, useRef, useState } from 'react';

interface MaxPriceSliderProps {
  value: number | undefined;
  onChange: (next: number | undefined) => void;
  min?: number;
  max?: number;
  step?: number;
}

export const MaxPriceSlider = ({
  value,
  onChange,
  min = 0,
  max = 500,
  step = 5,
}: MaxPriceSliderProps) => {
  const [local, setLocal] = useState<number>(value ?? max);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Keep local state in sync if URL changes externally — but not while a
  // debounced change is still pending, or the stale URL value drags the thumb
  // back mid-drag.
  useEffect(() => {
    if (debounceRef.current) return;
    setLocal(value ?? max);
  }, [value, max]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = Number.parseInt(e.target.value, 10);
    setLocal(next);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      debounceRef.current = null;
      // Treat "max" as "no filter"
      onChange(next >= max ? undefined : next);
    }, 200);
  };

  const display = local >= max ? `Any price` : local === 0 ? 'Free' : `Up to $${local}`;

  return (
    <div className="flex items-center gap-3 px-3 py-1.5 rounded-full bg-gray-50 border border-gray-200">
      {/* Fixed width + tabular figures: the label's width used to change with the
          value ("Free" → "Up to $5"), shifting the track under the cursor so
          the thumb jumped, worst at the low end where the label changes most. */}
      <span className="w-[5.25rem] shrink-0 text-xs font-sans tracking-widest uppercase tabular-nums text-gray-700 whitespace-nowrap">
        {display}
      </span>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={local}
        onChange={handleChange}
        className="w-32 h-1 bg-gray-300 rounded-full appearance-none cursor-pointer accent-primary-700"
        aria-label="Maximum tasting price"
      />
    </div>
  );
};
